import jsonata from 'jsonata';
import * as Handlebars from 'handlebars';

/**
 * Standardized file names for definition template version control.
 */
export enum DefinitionFileNames {
  PageTemplate = 'pageTemplate',
  DataCardTemplate = 'dataCardTemplate',
}

/**
 * Interface defining a reference to a page within an app configuration.
 */
export interface AppPageReference {
  pageId: string;
  pageName?: string;
  route?: string;
}

/**
 * Interface defining the overall structure of a dynamic application.
 */
export interface AppConfig {
  appName: string;
  appId: string;
  appDescription?: string;
  appIcon?: string;
  pages: AppPageReference[] | any[]; // Accepts references or fallback inline pages
}

/**
 * Interface defining the complete schema for a dynamically generated page.
 */
export interface PageConfig {
  pageId?: string; // UUID used to fetch the template file or configurations
  pageName: string;
  route: string;
  dataVariables: DataVariable[];
  dataTransformations: DataTransformation[];
  template: string; // Handlebars HTML string
  events: AppEvent[];
}

/**
 * Interface defining a data source required by the template.
 */
export interface DataVariable {
  variableName: string;
  type: 'endpoint' | 'static' | 'localStorage' | 'clientLocalState';
  description?: string;
  payload: {
    http?: {
      method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      path: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params?: { filter?: any };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      body?: any;
      returnType?: string;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value?: any;
    key?: string;
  };
}

/**
 * Interface defining a JSONata transformation step.
 */
export interface DataTransformation {
  variableName: string; // The new key added to the Root Data Object
  description?: string;
  expression: string; // Valid JSONata expression
}

/**
 * Interface defining an interactive event to be mapped from the UI.
 */
export interface AppEvent {
  eventName: string; // MUST match the `data-action` attribute in the template
  type:
    | 'endpoint'
    | 'navigate'
    | 'showNotification'
    | 'refreshData'
    | 'updateState'
    | 'showPageModal'
    | 'showPageDrawer'
    | 'showPageSplitter';
  description?: string;
  payload: {
    http?: {
      method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      path: string; // Can use {{id}}, {{targetValue}}, or any state {{variable}} placeholders
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      body?: any;
    };
    route?: string;
    notification?: {
      type: 'success' | 'error' | 'info' | 'warning';
      message: string;
    };
    variablesToRefresh?: string[]; // Array of dataVariable names to re-fetch
    stateUpdate?: Record<string, any>; // Key-value pairs to update natively in the page state
    pageModal?: {
      route: string; // The route/key of the target AppPage
      payload?: Record<string, any>; // The data payload to pass to the modal page
      title?: string; // Modal title
      width?: number | string; // Modal width
    };
    pageDrawer?: {
      route: string; // The route/key of the target AppPage
      payload?: Record<string, any>; // The data payload to pass to the drawer page
      title?: string; // Drawer title
      placement?: 'left' | 'right' | 'top' | 'bottom'; // Drawer placement direction
      size?: 'default' | 'large'; // Preset drawer size
      width?: number | string; // Custom drawer width (overrides size if provided)
    };
    pageSplitter?: {
      route: string; // The route/key of the target AppPage
      payload?: Record<string, any>; // The data payload to pass to the child page
      title?: string; // Splitter header title
      icon?: string; // FontAwesome icon class for the header
      defaultSize?: number | string; // Starting size of the child pane (e.g., '30%', 400)
      min?: number | string; // Minimum size bound
      max?: number | string; // Maximum size bound
      resizable?: boolean; // Whether the user can drag the splitter bar
      layout?: 'horizontal' | 'vertical'; // Split direction
    };
  };
  onSuccessEvents?: string[]; // Events to trigger sequentially upon successful execution
  onErrorEvents?: string[]; // Events to trigger if execution fails
}

/**
 * PURE FUNCTION: Formats a PageConfig object specifically for read-only JSON display.
 * Strips out large string payloads like the HTML Template to keep the preview clean.
 * @param {PageConfig} config - The raw page configuration.
 * @returns {Partial<PageConfig>} The sanitized configuration.
 */
export function formatPageConfigForDisplay(
  config: PageConfig,
): Partial<PageConfig> {
  if (!config) return config;
  const { template, ...displayConfig } = config;
  return displayConfig;
}

/**
 * PURE FUNCTION: Applies a series of JSONata transformations sequentially against a context data object.
 * Each transformation's output is injected back into the context, allowing subsequent expressions to reference it.
 * CRITICAL FIX: Enforces a null fallback for undefined results so that JSON.stringify retains the keys
 * in the Monaco Editor preview, and captures errors explicitly in the payload.
 * @param {DataTransformation[]} transformations - The array of transformations to apply.
 * @param {Record<string, any>} initialContext - The starting data object context.
 * @returns {Promise<Record<string, any>>} A new context object containing the final transformed state.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function applyDataTransformations(
  transformations: DataTransformation[],
  initialContext: Record<string, any>,
): Promise<Record<string, any>> {
  if (!transformations || transformations.length === 0) return initialContext;

  const context = { ...initialContext };

  for (const t of transformations) {
    if (!t.expression || !t.variableName) continue;
    try {
      const expr = jsonata(t.expression);
      const result = await expr.evaluate(context);

      // JSONata evaluates to `undefined` if no match is found.
      // `JSON.stringify` drops `undefined` properties entirely.
      // We cast to `null` so it remains visible in the Monaco Editor!
      context[t.variableName] = result !== undefined ? result : null;
    } catch (err: any) {
      console.error(
        `[DynamicPageRenderer] JSONata Error in transformation "${t.variableName}":`,
        err,
      );
      // Bind the error into the payload so the developer can see exactly why the transformation failed
      context[t.variableName] = {
        error: `Transformation failed: ${err.message || String(err)}`,
      };
    }
  }

  return context;
}

/**
 * PURE FUNCTION: Interpolates specific contextual placeholders inside a string using Handlebars.
 * This automatically resolves states like {{username}} natively based on context memory.
 * @param {string} template - The string containing placeholders.
 * @param {Record<string, any>} context - The dictionary of variables mapped to state.
 * @returns {string} The interpolated string.
 */
export function interpolateStringWithContext(
  template: string,
  context: Record<string, any>,
): string {
  if (!template || typeof template !== 'string') return template;
  try {
    const compiler = Handlebars.compile(template, {
      noEscape: true,
      strict: false,
    });
    return compiler(context);
  } catch (e) {
    console.warn('[DynamicPageRenderer] String interpolation failed:', e);
    return template;
  }
}

/**
 * PURE FUNCTION: Deeply interpolates specific placeholders inside an object/array structure using context.
 * Automatically drops keys that resolve to the '__RAPIDER_OMIT__' token, allowing for perfectly clean PATCH payloads.
 * @param {any} obj - The target object, array, or primitive.
 * @param {Record<string, any>} context - The dictionary of variables mapped to state.
 * @returns {any} A deeply interpolated clone of the payload.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function interpolateObjectWithContext(
  obj: any,
  context: Record<string, any>,
): any {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === 'string') {
    const interpolated = interpolateStringWithContext(obj, context);
    return interpolated === '__RAPIDER_OMIT__' ? undefined : interpolated;
  }

  if (Array.isArray(obj)) {
    return obj
      .map((item) => interpolateObjectWithContext(item, context))
      .filter((item) => item !== undefined);
  }

  if (typeof obj === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const resolvedValue = interpolateObjectWithContext(obj[key], context);
        if (resolvedValue !== undefined) {
          result[key] = resolvedValue;
        }
      }
    }
    return result;
  }

  return obj;
}

/**
 * PURE FUNCTION: Interpolates specific contextual placeholders (`{{id}}` and `{{targetValue}}`) inside a string.
 * @param {string} template - The string containing placeholders.
 * @param {string} dataId - The value to substitute for `{{id}}`.
 * @param {string} targetValue - The value to substitute for `{{targetValue}}`.
 * @returns {string} The interpolated string.
 */
export function interpolateString(
  template: string,
  dataId: string,
  targetValue: string,
): string {
  if (!template || typeof template !== 'string') return template;
  return template
    .replace(/\{\{id\}\}/g, dataId || '')
    .replace(/\{\{targetValue\}\}/g, targetValue || '');
}

/**
 * PURE FUNCTION: Deeply interpolates specific placeholders inside an object/array structure.
 * Useful for resolving contextual IDs inside complex HTTP body payloads before executing requests.
 * @param {any} obj - The target object, array, or primitive.
 * @param {string} dataId - The value to substitute for `{{id}}`.
 * @param {string} targetValue - The value to substitute for `{{targetValue}}`.
 * @returns {any} A deeply interpolated clone of the payload.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function interpolateObject(
  obj: any,
  dataId: string,
  targetValue: string,
): any {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === 'string') {
    return interpolateString(obj, dataId, targetValue);
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => interpolateObject(item, dataId, targetValue));
  }

  if (typeof obj === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = interpolateObject(obj[key], dataId, targetValue);
      }
    }
    return result;
  }

  return obj;
}

/**
 * PURE FUNCTION: Constructs a URL path by properly appending stringified JSON filters.
 * @param {string} path - The base URL path.
 * @param {any} params - The params object containing a `filter` key.
 * @returns {string} The resolved URL path.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildFilterPath(
  path: string,
  params?: { filter?: any },
): string {
  if (!params || !params.filter) return path;

  let filterStr = params.filter;
  if (typeof filterStr === 'object') {
    filterStr = JSON.stringify(filterStr);
  }

  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}filter=${encodeURIComponent(filterStr)}`;
}

/**
 * PURE FUNCTION: Builds the Loopback path for fetching ClientLocalState by key securely.
 * @param {string} key - The unique state key mapping to the local state record.
 * @returns {string} The formatted URL path.
 */
export function buildClientLocalStatePath(key: string): string {
  const filter = { where: { key } };
  return `/client-local-states?filter=${encodeURIComponent(JSON.stringify(filter))}`;
}

/**
 * PURE FUNCTION: Builds the local filePath parameter for retrieving a specific page's HTML template via the SDK.
 * @param {string} pageId - The ID (UUID) of the page template to load.
 * @returns {string} The formatted file path (e.g., 'uuid.html').
 */
export function buildPageTemplateFilePath(pageId: string): string {
  if (!pageId) return '';
  return `${pageId}.html`;
}

export const DYNAMIC_PAGE_CONFIG = {
  texts: {
    loading: $localize`:@@dynamicPage.loading:Loading page data...`,
    errorPrefix: $localize`:@@dynamicPage.errorPrefix:An error occurred:`,
    retryButton: $localize`:@@dynamicPage.retryButton:Retry`,
    defaultError: $localize`:@@dynamicPage.defaultError:Failed to load data.`,
    actionFailed: $localize`:@@dynamicPage.actionFailed:Action failed to execute.`,

    // UI Mode Toggle Texts
    tabPageConfig: $localize`:@@dynamicPage.tabPageConfig:Page Config`,
    tabHtml: $localize`:@@dynamicPage.tabHtml:Page Template`,
    tabData: $localize`:@@dynamicPage.tabData:Page Data`,
    viewPage: $localize`:@@dynamicPage.viewPage:Page`,
    viewCode: $localize`:@@dynamicPage.viewCode:Code`,
  },
};

export const DYNAMIC_APP_VIEWER_CONFIG = {
  texts: {
    loadingApp: $localize`:@@appViewer.loadingApp:Loading Application...`,
    noAppId: $localize`:@@appViewer.noAppId:No Application ID provided.`,
    noPages: $localize`:@@appViewer.noPages:This application has no pages defined.`,
    loadFailed: $localize`:@@appViewer.loadFailed:Failed to load application configuration.`,
    pageLoadFailed: $localize`:@@appViewer.pageLoadFailed:Failed to load page configuration.`,
    templateLoadFailed: $localize`:@@appViewer.templateLoadFailed:Failed to load page template.`,
    missingPageId: $localize`:@@appViewer.missingPageId:First page is missing a valid page ID.`,
  },
};
